import { MoovConfigurationDTO } from "../DTO/moovConfigurationDTO";
import { MoovDefinition } from "./moovDefinition";
import { MoovStep } from "./moovStep";
import { ConfigurationParameter, MoovStepConfiguration } from "./moovStepConfiguration";
import { Observable, Subject, take } from "rxjs";
import { AbstractParameter, PresetValues } from "./preset";
import { MoovStepPremierServiceConfiguration, ServiceConfigurationParameter } from "./moovStepPremierServiceConfiguration";
import { PremierServiceProduct } from "./premierServiceProduct";
import { CustomerPremierPartnerServiceConfiguration } from "./customerPremierPartnerServiceConfiguration";
import { PremierService } from "./premierService";
import { PremierPartner } from "./premierPartner";
import { isValidDate } from "../theme/utils/app-validators";

export class MoovConfiguration{
    public id!: number;
    public moovDefinitionId!: number;
    public moovDefinition!: MoovDefinition;
    public customerId!: number;
    public clientId!: number;
    public status!: number; //0=Draft, 1=Testing, 2=Active, 3=Deleted
    public expectedFrequency!: string;
    public filenameStartsWith!: string;
    public filenameContains!: string;
    public stepConfigurations!: MoovStepConfiguration[];
    public added!: Date;
    public edited!: Date;
    public deleted!: boolean;
    public uniqueIdentifier!: string;
    public presetValues!: PresetValues;
    public usagePriceOverride!: number;

    public isQBOAuthorized?:boolean; //client-side helper attribute
    public isXeroAuthorized?:boolean; //client-side helper attribute
    public isDropboxAuthorized?:boolean; //client-side helper attribute
    public isMicrosoftAuthorized?:boolean; //client-side helper attribute

    public presetChangedSubject = new Subject<number>()

    constructor(){
        this.stepConfigurations = new Array();
    }

    public static mapFromDTO(param:MoovConfigurationDTO):MoovConfiguration{ 
        if (!param) throw new Error("parameter is null")
        let obj = new MoovConfiguration();
        try
        {
            obj.id = param.id!;
            obj.moovDefinitionId = param.moovDefinitionId;
            obj.customerId = param.customerId;
            obj.clientId = param.clientId!;
            obj.status=param.status;
            obj.expectedFrequency = param.expectedFrequency!;
            obj.filenameStartsWith = param.filenameStartsWith!;
            obj.filenameContains = param.filenameContains!;
            obj.stepConfigurations = param.stepConfigurations.map(msc=>MoovStepConfiguration.mapFromDTO(msc));
            if (param.added) obj.added = new Date(param.added);
            if (param.edited) obj.edited = new Date(param.edited);
            obj.deleted = param.deleted;
            obj.uniqueIdentifier = param.uniqueIdentifier!;
            if(param.presetValues) obj.presetValues = Object.assign(new PresetValues(),JSON.parse(param.presetValues));
            obj.usagePriceOverride = param.usagePriceOverride!;
        }
        catch(e)
        {
            console.log(e);
            
        }
        return obj;
    }

    public static mapToDTO(param:MoovConfiguration):MoovConfigurationDTO{ 
        if (!param) throw new Error("parameter is null")
        let dto = new MoovConfigurationDTO();
        try
        {
            dto.id = param.id;
            dto.moovDefinitionId = param.moovDefinition.id!;
            dto.customerId = param.customerId;
            dto.clientId = param.clientId;
            dto.status=param.status;
            dto.expectedFrequency = param.expectedFrequency;
            dto.filenameStartsWith = param.filenameStartsWith;
            dto.filenameContains = param.filenameContains;
            dto.stepConfigurations = param.stepConfigurations.map(msc=>MoovStepConfiguration.mapToDTO(msc));
            if (isValidDate(param.added)) dto.added = param.added.toISOString();
            if (isValidDate(param.edited)) dto.edited = param.edited.toISOString();
            dto.deleted = param.deleted;
            dto.uniqueIdentifier = param.uniqueIdentifier;
            if(param.presetValues) dto.presetValues = JSON.stringify(param.presetValues);
            dto.usagePriceOverride = param.usagePriceOverride;
        }
        catch(e)
        {
            console.log(e);
            
        }
        return dto;
    }

    get displayStatus(){
        switch(this.status){
            case 0:
                return "Draft";
            case 1:
                return "Test";
            case 2:
                return "Active";
            case 3:
                return "Inactive";
            default:
                return "";
        }
    }

    get clone():MoovConfiguration{
        //use DTO to create a deep copy
        let dto=MoovConfiguration.mapToDTO(this);
        dto.id=undefined;
        dto.clientId=undefined;
        dto.status=0;
        dto.filenameStartsWith=undefined;
        dto.filenameContains=undefined;
        dto.expectedFrequency=undefined;
        dto.added=undefined;
        dto.edited=undefined;
        dto.deleted=false;
        dto.uniqueIdentifier=undefined;
        dto.presetValues=undefined;
        var mc = MoovConfiguration.mapFromDTO(dto);
        mc.moovDefinition = this.moovDefinition;
        mc.stepConfigurations?.forEach(sc => {
            sc.id=undefined;
            sc.moovStep = mc.moovDefinition.steps.find(s => s.id == sc.moovStepId);
            sc.premierServiceConfigurations=[]; //we don't want to clone these
            if(sc.moovStep!.moovStepDefinition.id==2 || sc.moovStep!.moovStepDefinition.id==6){
                //reset to default values provided by moovStep
                sc.parameters=[];
            }
        });
        return mc;
    }

    get hasPremierSteps(){
        return this.moovDefinition?.steps.findIndex(ms => ms.moovStepDefinition.premierPartnerRestriction!>0)>-1;
    }

    public isOwner(entityType:string, entityId:number){
        return this.customerId==entityId; //currently entityType has no relevance here as configurations can only be owned by customers
    }

    public matchesFileName(value:string){
        return value.toLowerCase().startsWith(this.filenameStartsWith.toLowerCase()) && value.toLowerCase().includes(this.filenameContains.toLowerCase());
    }

    public getPresetInputType(name:string){
        return this.moovDefinition.presets?.find(p=>p.name==name)?.inputType;
    }

    public getPresetOptions(name:string){
        return this.moovDefinition.presets?.find(p=>p.name==name)?.options;
    }

    public applyPremierConfigurationPreset(cppscPreset:CustomerPremierPartnerServiceConfiguration, ppObservable:Observable<PremierPartner[]>){
        let premierMoovSteps = this.moovDefinition.steps.filter(ms=>ms.moovStepDefinition.premierPartnerRestriction===cppscPreset.premierPartnerId);
        premierMoovSteps?.forEach(pms => {
            let applied:boolean=false;
            //create a premier service configuration preset
            //find matching premierservice product, customerpremierpartnerconfig and premier service first
            let premierPartner:PremierPartner;
            let premierService:PremierService;
            let premierServiceProduct:PremierServiceProduct;
            ppObservable.pipe(take(1)).subscribe(ppArray=> {
                for(var pp of ppArray){
                    for(var ps of pp.premierServices){
                        premierServiceProduct = ps.productsList.find(psp => psp.restrictedToMoovStepDefinitionId===pms.moovStepDefinitionId)!;
                        if(premierServiceProduct){
                            premierService=ps;
                            premierPartner=pp;
                            break;
                        }
                    }
                    if(premierService) break;
                }
                //now we found the matching premier service and need to find a matching customer configuration => if a customer has more than one configuration for the same partner and service, then we take the first one
                applied = this.applyPremierServiceConfigurationPreset(undefined, undefined, pms, premierServiceProduct, cppscPreset, premierService);
                if (applied) {
                    this.raisePresetChanged(pms.id);
                    console.log('preset applied');
                }
            });
        });
    }

    public applyPreset(name:string, value:string, cppscPreset:CustomerPremierPartnerServiceConfiguration, cppscObservable:Observable<CustomerPremierPartnerServiceConfiguration[]>, ppObservable:Observable<PremierPartner[]>){
        let preset = this.moovDefinition.presets?.find(ps=>ps.name===name);
        preset?.parameters.forEach(ap=>{
            //find related moov step and apply preset to config or if preset has no value, remove to trigger default set again
            let moovStep = this.moovDefinition.steps.find(ms=>ms.moovStepDefinitionId===ap.moovStepDefinitionId && ms.sequenceNo===ap.moovStepSequenceNo);
            if(moovStep){
                let applied:boolean=false;
                if(ap.parameterSource==="msd"){
                    //create a moov step configuration preset
                    applied = this.applyMoovStepConfigurationPreset(ap,value,moovStep);
                    if (applied) {
                        this.raisePresetChanged(moovStep.id);
                        console.log('preset applied');
                    }
                }
                else if(ap.parameterSource==="psp"){
                    //create a premier service configuration preset
                    //find matching premierservice product, customerpremierpartnerconfig and premier service first
                    let premierPartner:PremierPartner;
                    let premierService:PremierService;
                    let premierServiceProduct:PremierServiceProduct;
                    ppObservable.pipe(take(1)).subscribe(ppArray=> {
                        for(var pp of ppArray){
                            for(var ps of pp.premierServices){
                                premierServiceProduct = ps.productsList.find(psp => psp.id===ap.premierServiceProductId)!;
                                if(premierServiceProduct){
                                    premierService=ps;
                                    premierPartner=pp;
                                    break;
                                }
                            }
                            if(premierService) break;
                        }
                        //now we found the matching premier service and need to find a matching customer configuration => if a customer has more than one configuration for the same partner and service, then we take the first one
                        if(premierPartner && premierService){
                            if(cppscPreset){
                                //cppsc preset was given
                                applied = this.applyPremierServiceConfigurationPreset(ap, value, moovStep, premierServiceProduct, cppscPreset, premierService);
                                if (applied) {
                                    this.raisePresetChanged(moovStep.id);
                                    console.log('preset applied');
                                }
                            } 
                            else{
                                //cppsc preset was not given
                                cppscObservable.pipe(take(1)).subscribe(cppscArray =>{
                                    cppscPreset = cppscArray.find(cppsc => cppsc.premierPartnerId==premierPartner.id && cppsc.premierServiceId==premierService.id)!;
                                    if(cppscPreset) {
                                        applied = this.applyPremierServiceConfigurationPreset(ap, value, moovStep, premierServiceProduct, cppscPreset, premierService);
                                        if (applied) {
                                            this.raisePresetChanged(moovStep.id);
                                            console.log('preset applied');
                                        }
                                    }
                                });
                            }
                        } 
                    });
                }
            }
        });
    }

    private applyMoovStepConfigurationPreset(ap:AbstractParameter, value:string, moovStep:MoovStep):boolean{
        let applied:boolean=false;
        let moovStepConfiguration = this.stepConfigurations.find(msc => msc.moovStepId===moovStep.id);
        if(value && value.length>0){
            //preset has a value => apply it
            if(!moovStepConfiguration){
                moovStepConfiguration = this.createNewMoovStepConfiguration(moovStep);
                this.stepConfigurations.push(moovStepConfiguration);
            } 
            let existingParameter = moovStepConfiguration.parameters.find(p=>p.name==ap.name);
            if(!existingParameter){
                existingParameter = new ConfigurationParameter();
                existingParameter.name=ap.name;
                moovStepConfiguration.parameters.push(existingParameter);
            }
            if(existingParameter.value != value){
                existingParameter.value=value;
                moovStepConfiguration.raisePresetChanged(moovStep.id);
                applied=true;
            }
        }
        else if(moovStepConfiguration){
            //preset has no value, and there is a moovstep configuration => remove parameter and if moovstep configuration has no more parameters, remove moovstep configuration to revert to defaults
            let index = moovStepConfiguration.parameters.findIndex(p=>p.name==ap.name);
            if(index>-1) moovStepConfiguration.parameters.splice(index,1);
            if(moovStepConfiguration.parameters.length==0 && (!moovStepConfiguration.premierServiceConfigurations || moovStepConfiguration.premierServiceConfigurations.length==0)) {
                let index = this.stepConfigurations.indexOf(moovStepConfiguration);
                if(index>-1) this.stepConfigurations.splice(index,1);
            }
            moovStepConfiguration.raisePresetChanged(moovStep.id);
            applied=true;
        }
        return applied;
    }

    private applyPremierServiceConfigurationPreset(ap:AbstractParameter | undefined, value:string | undefined, moovStep:MoovStep, psp:PremierServiceProduct, cppsc:CustomerPremierPartnerServiceConfiguration, ps:PremierService):boolean{
        let applied:boolean=false;
        let moovStepConfiguration = this.stepConfigurations.find(msc => msc.moovStepId===moovStep.id);
        let premierServiceConfiguration = moovStepConfiguration?.premierServiceConfigurations?.find(psc=>psc.premierServiceProductId===psp.id);
        if(cppsc){
            //if a new cppsc has to be processed
            if(!moovStepConfiguration){
                moovStepConfiguration = this.createNewMoovStepConfiguration(moovStep);
                this.stepConfigurations.push(moovStepConfiguration);
            }
            if(!premierServiceConfiguration){
                premierServiceConfiguration=this.createNewPremierServiceConfiguration(moovStepConfiguration,psp,cppsc,ps);
                moovStepConfiguration.premierServiceConfigurations.push(premierServiceConfiguration);
                applied=true;
            }
            if(premierServiceConfiguration.customerPremierPartnerServiceConfigurationId!=cppsc.id){
                premierServiceConfiguration.customerPremierPartnerServiceConfigurationId=cppsc.id;
                applied=true;
            } 
        }
        if(value && value.length>0){
            //preset has a value => apply it
            if(!moovStepConfiguration){
                moovStepConfiguration = this.createNewMoovStepConfiguration(moovStep);
                this.stepConfigurations.push(moovStepConfiguration);
            }
            if(!premierServiceConfiguration){
                premierServiceConfiguration=this.createNewPremierServiceConfiguration(moovStepConfiguration,psp,cppsc,ps);
                moovStepConfiguration.premierServiceConfigurations.push(premierServiceConfiguration);
                applied=true;
            }
            if(premierServiceConfiguration.customerPremierPartnerServiceConfigurationId!=cppsc.id) {
                premierServiceConfiguration.customerPremierPartnerServiceConfigurationId=cppsc.id;
                applied=true;
            }
            if(ap){
                let existingParameter = premierServiceConfiguration.parameters.find(p=>p.name===ap.name);
                if(!existingParameter){
                    existingParameter = new ServiceConfigurationParameter();
                    existingParameter.name=ap.name;
                    premierServiceConfiguration.parameters.push(existingParameter);
                }
                if(existingParameter.value!=value){
                    existingParameter.value=value;
                    applied=true;
                }
            }
        }
        else if(moovStepConfiguration && ap){
            let premierServiceConfiguration = moovStepConfiguration.premierServiceConfigurations.find(psc=>psc.premierServiceProductId===psp.id);
            if(premierServiceConfiguration){
                //preset has no value, and there is a premier service configuration => remove from premier config
                let index = premierServiceConfiguration.parameters.findIndex(p=>p.name===ap.name);
                if(index>-1) premierServiceConfiguration.parameters.splice(index,1);
                if(premierServiceConfiguration.parameters.length===0) {
                    let index = moovStepConfiguration.premierServiceConfigurations.indexOf(premierServiceConfiguration);
                    if(index>-1) moovStepConfiguration.premierServiceConfigurations.splice(index,1);
                }
                applied=true;
            }
        }
        if(applied) moovStepConfiguration!.raisePresetChanged(moovStep.id);
        return applied;
    }

    public createNewMoovStepConfiguration(moovStep:MoovStep):MoovStepConfiguration{
        let msc = new MoovStepConfiguration();
        msc.id=0;
        msc.moovStep=moovStep;
        msc.moovStepId=moovStep.id;
        msc.moovConfigurationId=this.id;
        msc.delayBy=0;
        msc.parameterOptionId=moovStep.parameterOptionId;
        msc.skipAlways=moovStep.canSkipAlways!;
        msc.skipIfNoData=moovStep.canSkipIfNoData!;
        return msc;
    }

    public createNewPremierServiceConfiguration(moovStepConfiguration:MoovStepConfiguration, psp:PremierServiceProduct, cppsc:CustomerPremierPartnerServiceConfiguration, ps:PremierService){
        let mscps = new MoovStepPremierServiceConfiguration();
        mscps.id=0;
        mscps.moovStepConfigurationId=moovStepConfiguration.id!; 
        mscps.premierServiceId=psp.serviceId;
        mscps.premierServiceProductId=psp.id;
        mscps.customerPremierPartnerServiceConfigurationId=cppsc.id;
        mscps.premierPartnerId=cppsc.premierPartnerId;
        mscps.premierServiceType=ps.serviceTypeId;
        mscps.premierServiceProductType=psp.productTypeId;
        mscps.status=2; //set active
        return mscps;
    }

    public raisePresetChanged(moovStepId:number){
        this.presetChangedSubject.next(moovStepId);
    }
}